import * as React from 'react';
import { dismissModal, getOpenModal, openItemPageFirstTimeModal, openLoginModal } from '@/redux/modules/modal';
import { FormattedMessage, useIntl } from 'react-intl';
import { getAuthToken, isUserLoggedIn } from '@/redux/modules/user';
import { getCreateAccountState, submitCreateAccount } from '@/redux/modules/createAccount';
import { getIdFromPath, getMediumItemImageUrl, getUpcomingTitleSearchParams, isItemPage } from '@/utils/urls';
import { useDispatch, useSelector } from 'react-redux';
import exitIntent from '@/utils/exitIntent';
import FollowSearchForm from '../FollowSearch/FollowSearchForm';
import { getAttributionData } from '@/redux/modules/analytics';
import { getItem } from '@/redux/modules/item';
import { getItemFacets } from '@/redux/modules/itemFacets';
import ImageWithFallback from '@liveauctioneers/caterwaul-components/lib/ImageWithFallback/ImageWithFallback2';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import Modal from '@liveauctioneers/caterwaul-components/lib/Modal/Modal';
import ModalHeader from '@liveauctioneers/caterwaul-components/lib/Modal/ModalHeader';
import { modalTypes } from '@/enums';
import styled from 'styled-components';
import { submitSaveSearch } from '@/redux/modules/saveSearch';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import useIsWiderThan from '@/hooks/useIsWiderThan';
import { useLocation } from 'react-router-dom';

const ItemPageFirstTimeModal = () => {
    const intl = useIntl();

    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const accountState = useSelector(getCreateAccountState, isEqual);
    const attributionData = useSelector(getAttributionData, isEqual);
    const wide = useIsWiderThan(600);
    const itemPage = isItemPage(pathname);
    const itemId = getIdFromPath(pathname);

    const { catalogId, imageNumber = 1, sellerId, title = '' } = useSelector(
        (state) => getItem(state, itemId),
        isEqual
    );
    const facets = useSelector((state) => getItemFacets(state, itemId), isEqual);
    const imageUrl = getMediumItemImageUrl(sellerId, catalogId, itemId, imageNumber, 1);

    const [email, setEmail] = React.useState(''); // eslint-disable-line
    const [exiting, setExiting] = React.useState(false);
    const [savedSearch, setSavedSearch] = React.useState(false);
    const [modalPopped, setModalPopped] = React.useState(false);
    const [modalSeen, setModalSeen] = React.useState(true);
    const isModalOpen = useSelector(getOpenModal, isEqual) === modalTypes.ITEM_PAGE_FIRST_TIME;
    const userLoggedIn = useSelector(isUserLoggedIn, isEqual);
    const authToken = useSelector(getAuthToken, isEqual);

    // exit intent timer, on small screens pop after 40 seconds, 60 on large
    // after allotted second, popup will appear
    let timer = null;
    if (!modalPopped && itemPage && !userLoggedIn) {
        const milliSecondsToFire = wide ? 60000 : 40000;
        timer = setTimeout(function () {
            setExiting(true);
        }, milliSecondsToFire);
    }

    const automaticallyPopModal = () => {
        dispatch(openItemPageFirstTimeModal());
        setModalPopped(true);
    };

    const createAccount = (email, agree) => {
        dispatch(submitCreateAccount(email, agree, attributionData));
    };

    React.useEffect(() => {
        // if they've never seen the modal before, they have some exit intent
        // they are not logged in and the modal has fired this session, open it up
        if (!modalSeen && exiting && !userLoggedIn && itemPage && !modalPopped && typeof window !== 'undefined') {
            window.setTimeout(automaticallyPopModal, 100);
            window.localStorage.itemPageFirstTimeModalSeen = true;
        }
        if (typeof window !== 'undefined') {
            setModalSeen(Boolean(window.localStorage.itemPageFirstTimeModalSeen));
            if (!exiting && !modalSeen && itemPage && !userLoggedIn) {
                exitIntent(setExiting);
            } else {
                if (timer) {
                    clearTimeout(timer);
                }
            }
        }
        if (authToken && modalPopped && exiting && sellerId && !savedSearch) {
            const saveSearchParams = getUpcomingTitleSearchParams(title, sellerId);
            saveSearchParams.status = 'online';
            dispatch(submitSaveSearch(saveSearchParams, true));
            setSavedSearch(true);
        }
    }, [modalPopped, itemPage, modalSeen, exiting, authToken]); // eslint-disable-line

    if (!isModalOpen) {
        return null;
    }

    const login = () => {
        dispatch(openLoginModal());
    };

    const generateTitle = () => {
        if (facets?.creators?.length) {
            return (
                <StyledHeader>
                    <StyledImageWithFallback alt={title || 'Item'} src={imageUrl} />
                    <Text h2>
                        {`${intl.formatMessage({ id: 'itemPageFirstTimeModal.huntingFor' })} ${
                            facets.creators[0].l1CategoryName
                        }?`}
                    </Text>
                </StyledHeader>
            );
        }
        return (
            <StyledHeader>
                <StyledImageWithFallback alt={title || 'Item'} src={imageUrl} />
                <Text h2>{intl.formatMessage({ id: 'itemPageFirstTimeModal.onTheHunt' })}</Text>
            </StyledHeader>
        );
    };

    const modalBody = () => {
        return (
            <>
                <StyledText secondary>
                    <FormattedMessage id="itemPageFirstTimeModal.enterEmailBelow" />
                </StyledText>
                <FollowSearchForm
                    error={accountState.error}
                    preSubmitButtonText={<FormattedMessage id="followSearchModal.startNow" />}
                    setEmail={setEmail}
                    submit={createAccount}
                    submitted={accountState.submitted}
                />
                <StyledLoginPhrase>
                    <FormattedMessage
                        id="setPasswordModal.footer"
                        key="footer"
                        values={{
                            link: (
                                <StyledLink key="link" onClick={login}>
                                    <FormattedMessage id="setPasswordModal.footerLink" />
                                </StyledLink>
                            ),
                        }}
                    />
                </StyledLoginPhrase>
            </>
        );
    };

    return (
        <StyledModal
            body={modalBody()}
            contentLabel="itemPageFirstTimePopup"
            header={
                <ModalHeader
                    onClose={() => {
                        dispatch(dismissModal());
                    }}
                    title={generateTitle()}
                />
            }
            show
        />
    );
};

export default ItemPageFirstTimeModal;

const StyledModal = styled(Modal).attrs(() => ({
    bodyClassName: 'bodyClassName',
}))`
    .bodyClassName {
        padding: 16px 0 20px 0;
        margin: 0 auto;
        width: 100%;
        max-width: 400px;

        @media (max-width: 767px) {
            padding: 15px 20px 40px;
        }
    }
`;

const StyledHeader = styled.div`
    margin-top: -41px;
`;

const StyledText = styled(Text)`
    text-align: center;
    margin-bottom: -20px;
    display: block;
    & + div {
        padding: 0;
    }
`;

const StyledImageWithFallback = styled(ImageWithFallback)`
    max-height: 74px;
    max-width: 80%;
    margin-bottom: 25px;
`;

const StyledLoginPhrase = styled.div`
    text-align: center;
    margin: 60px auto 0 auto;
`;

const StyledLink = styled(Link)`
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
`;
